import type { WorkflowDefinition } from 'src/distribution_platforms/features/workflow_definition';
import { LdtOrderDetailFieldsFragment, OrderStatus } from 'src/types';
import { EditOrderGuardInput, EditOrderGuardResult } from 'src/types/loaders';

/** LDT order detail actions (order_actions.tsx renders these). */
export enum LdtOrderAction {
  SlideImageSection = 'SLIDE_IMAGE_SECTION',
  DisplayRequisition = 'DISPLAY_REQUISITION',
  UpdateFiles = 'UPDATE_FILES',
  EditOrder = 'EDIT_ORDER',
  Cancel = 'CANCEL',
  RefaxPathologyFiles = 'REFAX_PATHOLOGY_FILES',
  RevertToQcPending = 'REVERT_TO_QC_PENDING',
  ApproveQC = 'APPROVE_QC',
  RejectDraftReport = 'REJECT_DRAFT_REPORT',
  DisplayPatientReport = 'DISPLAY_PATIENT_REPORT',
  DisplayDeIdentifiedReport = 'DISPLAY_DE_IDENTIFIED_REPORT',
  ShareTestReport = 'SHARE_TEST_REPORT',
  ReviseReportMutation = 'REVISE_REPORT',
  FaxTestReportToPathologyLabMutation = 'FAX_TEST_REPORT_TO_PATHOLOGY_LAB',
  RefaxReportToOrderingProvider = 'REFAX_REPORT_TO_ORDERING_PROVIDER',
  ImageQcApprove = 'IMAGE_QC_APPROVE',
  ImageQcReject = 'IMAGE_QC_REJECT',
  DownloadSlideImageQuery = 'DOWNLOAD_SLIDE_IMAGE',
}

/** Context for getAvailableActions. */
export interface GetAvailableActionsContext {
  order: LdtOrderDetailFieldsFragment;
  missingInformationMessages: string[];
  hasValidationErrors: boolean;
  userInfoId: string;
}

/** Context for checkOrderValidationErrors (async). isEditingQCPendingOrder and specimenType are derived from order. */
export interface CheckOrderValidationErrorsContext {
  order: LdtOrderDetailFieldsFragment;
}

/**
 * LDT-specific workflow behavior (status filters, order loader guards, order actions, etc.).
 */
export interface LdtWorkflowDefinition extends WorkflowDefinition {
  /**
   * Whether this workflow shows the billing hold section on the order detail page.
   */
  showBillingHoldSection: boolean;

  /**
   * Whether this workflow shows the ordering provider section on the order detail page.
   */
  showOrderingProviderSection: () => boolean;

  /**
   * When a CS/Sales user saves a QC Pending order that has an AI report:
   * - true: show SaveOrderWithReportDialog and submit via UpdateRejectedOrderMutation (status revert).
   * - false: show RevisionReasonDialog and submit via UpdateLdtOrderMutation (normal update).
   *
   * Orders without an AI report always use RevisionReasonDialog regardless of this flag.
   */
  revertStatusOnSaveWithReport: boolean;

  /**
   * Returns the display name for an order status based on this workflow.
   */
  getStatusDisplayName(status: OrderStatus): string;

  /**
   * When loading the order edit route, determines if the user should be redirected away.
   * Returns null if the user may access the edit page; otherwise returns { redirectPath, errorMessage }.
   * The loader+page should call redirectAndNotify with the result.
   */
  getEditOrderRedirect(input: EditOrderGuardInput): EditOrderGuardResult;

  /**
   * Returns which order actions are available for this order (Draft, QC Pending, etc.).
   */
  getAvailableActions(context: GetAvailableActionsContext): LdtOrderAction[];

  /**
   * Missing-information messages for QC Pending (e.g. missing pathology lab).
   * Only relevant for CS/Sales; implementors can check isCustomerSuccessOrSalesUser().
   */
  getOrderMissingInfoMessages(order: LdtOrderDetailFieldsFragment): string[];

  /**
   * Whether the order has form/validation errors (e.g. biopsy form resolver).
   * Used to gate APPROVE_QC and show validation message in order status section.
   */
  checkOrderValidationErrors(
    context: CheckOrderValidationErrorsContext,
  ): Promise<boolean>;
}
